import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './travel-bookings.api';

import type {
  TravelBookingAdminCancelInput,
  TravelBookingAdminIssueInput,
  TravelBookingAdminListQuery,
  TravelBookingAdminRefundInput,
  TravelBookingReportQuery,
} from '@utazzo/shared';

export const QK = {
  all: ['travel-bookings'] as const,
  list: (query: TravelBookingAdminListQuery) => ['travel-bookings', 'list', query] as const,
  summary: ['travel-bookings', 'summary'] as const,
  detail: (id: string) => ['travel-bookings', 'detail', id] as const,
  refundQuote: (id: string) => ['travel-bookings', 'refund-quote', id] as const,
  cancelQuote: (id: string) => ['travel-bookings', 'cancel-quote', id] as const,
  report: (query: TravelBookingReportQuery) => ['travel-bookings', 'report', query] as const,
};

export const useTravelBookingsList = (query: TravelBookingAdminListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listTravelBookings(query),
    staleTime: 15_000,
    // The queues change under the operator - a guest pays, a supplier
    // settles a cancellation - so the list keeps itself honest without a
    // manual refresh.
    refetchInterval: 60_000,
  });

/** `enabled: false` for callers that only sometimes have the right to ask (the sidebar badge). */
export const useTravelBookingsSummary = (enabled = true) =>
  useQuery({
    queryKey: QK.summary,
    queryFn: api.getTravelBookingsSummary,
    enabled,
    staleTime: 15_000,
    refetchInterval: enabled ? 60_000 : false,
  });

export const useTravelBooking = (id: string | null, opts: { poll?: boolean } = {}) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => api.getTravelBooking(id!),
    enabled: !!id,
    staleTime: 5_000,
    // While a supplier answer is still settling (BOOKING) the page polls;
    // otherwise it sits still.
    refetchInterval: opts.poll ? 5_000 : false,
  });

export const useTravelBookingsReport = (query: TravelBookingReportQuery) =>
  useQuery({
    queryKey: QK.report(query),
    queryFn: () => api.getTravelBookingsReport(query),
    staleTime: 60_000,
  });

/**
 * The refund dialog's figures. Fetched fresh every time the dialog opens
 * (`staleTime: 0`): a refund raised from another tab a minute ago changes
 * the maximum, and a stale ceiling is how a double refund starts.
 */
export const useRefundQuote = (id: string | null) =>
  useQuery({
    queryKey: QK.refundQuote(id ?? ''),
    queryFn: () => api.getRefundQuote(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });

/** The supplier's cancellation charge right now. Never cached - it moves with the clock. */
export const useCancelQuote = (id: string | null) =>
  useQuery({
    queryKey: QK.cancelQuote(id ?? ''),
    queryFn: () => api.getCancelQuote(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

export const useRevealDocuments = () =>
  useMutation({
    mutationFn: ({ bookingId, travellerId }: { bookingId: string; travellerId: string }) =>
      api.revealTravellerDocuments(bookingId, travellerId),
    onError: () => toast.error('Could not load the document numbers.'),
  });

export const useRequoteTravelBooking = () =>
  useMutation({
    mutationFn: (id: string) => api.requoteTravelBooking(id),
  });

export const useIssueTravelBooking = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TravelBookingAdminIssueInput }) =>
      api.issueTravelBooking(id, input),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: QK.all });
      if (result.status === 'CONFIRMED') toast.success(result.message);
      else if (result.status === 'BOOKING') toast.info(result.message);
      else toast.warning(result.message);
    },
  });
};

export const useResendConfirmation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.resendTravelBookingConfirmation(id),
    onSuccess: (result, id) => {
      // Only the timeline changed (a CONFIRMATION_RESENT event); the lists
      // and the summary counts are untouched.
      void qc.invalidateQueries({ queryKey: QK.detail(id) });
      if (result.sent) toast.success(result.message);
      else toast.warning(result.message);
    },
  });
};

/**
 * Fetch the e-ticket PDF URL and open it in a new tab. Generated fresh on the
 * server each time, so it always carries the latest seats, meals and GST.
 */
export const useDownloadEticket = () =>
  useMutation({
    mutationFn: (id: string) => api.getTravelBookingEticket(id),
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onError: () => toast.error('Could not open the e-ticket. It may not be ready yet.'),
  });

/**
 * Fill in the payment-gateway fee on paid bookings that are missing it, so
 * net profit reflects the real cost of collecting the money. Refreshes the
 * lists and the summary once fees land.
 */
export const useBackfillGatewayFees = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.backfillGatewayFees(),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: QK.all });
      if (result.updated > 0) {
        toast.success(
          `Recorded gateway fees on ${result.updated} booking${result.updated === 1 ? '' : 's'}.`,
        );
      } else {
        toast.info(
          result.scanned > 0
            ? 'All paid bookings already have their gateway fee.'
            : 'No paid bookings needed a gateway fee.',
        );
      }
    },
    onError: () => toast.error('Could not sync gateway fees.'),
  });
};

export const useRefundTravelBooking = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TravelBookingAdminRefundInput }) =>
      api.refundTravelBooking(id, input),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: QK.all });
      // A released hold whose gateway refund failed comes back 200 with the
      // failure in the message - the status change stood. Read it as a warning.
      if (result.refundId) toast.success(result.message);
      else toast.warning(result.message, { duration: 12_000 });
    },
  });
};

export const useCancelTravelBooking = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TravelBookingAdminCancelInput }) =>
      api.cancelTravelBooking(id, input),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: QK.all });
      toast.success(result.message, { duration: 8_000 });
    },
  });
};

export const useDeleteDropOffs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.deleteDropOffs({ ids }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: QK.all });
      if (result.skipped.length === 0) {
        toast.success(`Deleted ${result.deleted} drop-off${result.deleted === 1 ? '' : 's'}.`);
      } else {
        toast.warning(
          `Deleted ${result.deleted}; ${result.skipped.length} kept: ${result.skipped[0]?.reason ?? ''}`,
          { duration: 10_000 },
        );
      }
    },
    onError: () => toast.error('Could not delete the selected drop-offs.'),
  });
};
